import CancellablePromise from '@/types/cancellable-promise';

export const createCancellablePromise = <T>(promise: Promise<T>): CancellablePromise<T> => {
  let hasCanceled = false;

  const wrappedPromise = new Promise<T>((resolve, reject) => {
    promise.then(val => {
      if (!hasCanceled) {
        resolve(val);
      }
    });
    promise.catch(error => {
      if (!hasCanceled) {
        reject(error);
      }
    });
  });

  return {
    promise: wrappedPromise,
    cancel() {
      hasCanceled = true;
    },
    hasCanceled: () => hasCanceled,
  };
};

/**
 * Higher-order function that wraps an async operation with loader management.
 * Automatically starts the loader at the beginning and stops it when the block exits (success, error, or return).
 *
 * @param loaderActionType The loader action type to start/stop
 * @param startLoaderAction Function to start the loader action
 * @param stopLoaderAction Function to stop the loader action
 * @param operation The async operation to execute with loader management
 * @param suppressErrors If true, catches and suppresses errors (returns undefined). If false, rethrows errors. Default: true
 * @returns The result of the operation, or undefined if operation returns early or throws an error (when suppressErrors is true)
 *
 * @example
 * // Suppress errors (default behavior)
 * const result = await withLoaderAction(
 *   ADD_PORTFOLIOS_TO_COMPARISON.type,
 *   startLoaderAction,
 *   stopLoaderAction,
 *   async () => {
 *     const portfolios = await loadPortfolios();
 *     if (!portfolios) return; // loader automatically stopped
 *     await processPortfolios(portfolios);
 *     return portfolios;
 *   }
 * );
 *
 * @example
 * // Rethrow errors for custom handling
 * try {
 *   await withLoaderAction(
 *     SAVE_PORTFOLIO.type,
 *     startLoaderAction,
 *     stopLoaderAction,
 *     async () => savePortfolio(data),
 *     false // rethrow errors
 *   );
 * } catch (error) {
 *   // Custom error handling
 * }
 */
export const withLoaderAction = async <T>(
  loaderActionType: string,
  startLoaderAction: (name: string) => void,
  stopLoaderAction: (name: string) => void,
  operation: () => Promise<T>,
  suppressErrors: boolean = true,
): Promise<T | undefined> => {
  startLoaderAction(loaderActionType);
  try {
    const result = await operation();
    return result;
  } catch (error) {
    if (suppressErrors) {
      // Error already handled by API layer, suppress to prevent unhandled rejection
      return undefined;
    }

    throw error;
  } finally {
    stopLoaderAction(loaderActionType);
  }
};
